import { QuestionnaireQuestion } from "../../Models/Surveys/SurveyDataModel";
import { GenericHideCustomLogicManager } from "./GenericHideCustomLogicManager";
import {ScipDataManager} from "../SurveyResponse/ScipDataManager";

export class ScipCustomLogicManager extends GenericHideCustomLogicManager {
    private readonly _containsSvhcSummaryDataKey = "reach_contains_svhc";
    private readonly _producedInEuScipNumberDependentAnswers: string[] = ["Both EU produced and imported", "EU imported", "EU produced"];
    private readonly _hasScipNumberScipNumberDependentAnswers: string[] = ["Yes"];

    private getContainsSvhcAnswer() {
        // This first question on the SCIP page maps to the reach SVHC regulation page.
        const parentQuestionOfFirstScipQuestion = this.getParentQuestion(this._questions[0]);

        if (!parentQuestionOfFirstScipQuestion) {
            return "No";
        }

        if (parentQuestionOfFirstScipQuestion.summaryFieldKey !== this._containsSvhcSummaryDataKey) {
            throw new Error(`The parent question of the first SCIP question should be ${this._containsSvhcSummaryDataKey}. Instead got ${parentQuestionOfFirstScipQuestion.summaryFieldKey}`);
        }

        return parentQuestionOfFirstScipQuestion.value;
    }

    public shouldShowScip = () => this.getContainsSvhcAnswer().toLowerCase() !== "no";

    protected getAdditionalQuestionIndicesToHide(question: QuestionnaireQuestion, questionIndex: number, indicesToHideOrShow: boolean[]): boolean[] {
        if (this.shouldShowScip()) {
            return super.getAdditionalQuestionIndicesToHide(question, questionIndex, indicesToHideOrShow);
        }

        // hide all, scip data not required or applicable
        return this._questions.map(q => true);
    }

    public getDisplayIndex(questionIndex: number): number {
        const resultIndex = questionIndex;
        let numberOfHiddenQuestions = this._questions.slice(0, questionIndex).filter(q => !this.getQuestionStatus(q).visible).length;

        return resultIndex - numberOfHiddenQuestions;
    }

    private getQuestionByScipData = (key: string) =>
        this._questions.find(q => q.scipDataKey === key)!;

    private getQuestionStatus(question: QuestionnaireQuestion) {
        const isArticleQuestion = this._questions.find(q => q.summaryFieldKey === ScipDataManager.IsArticleSummaryKey)!;
        const producedInEuQuestion = this.getQuestionByScipData(ScipDataManager.ScipDataKeyEuProduction);
        const hasScipNumberQuestion = this.getQuestionByScipData(ScipDataManager.HasScipNumber);

        const uniformStatus = (status: boolean) => ({
            required: status,
            visible: status
        });

        if (question.summaryFieldKey === ScipDataManager.IsArticleSummaryKey) {
            return uniformStatus(true);
        }
        else if (question.scipDataKey === ScipDataManager.ScipDataKeyEuProduction) {
            return uniformStatus(isArticleQuestion.value === "Article as such or Complex Object");
        }
        else if (question.scipDataKey === ScipDataManager.HasScipNumber) {
            return uniformStatus(
                (isArticleQuestion.value === "Article as such or Complex Object") &&
                (producedInEuQuestion.value === "No data")
            );
        }
        else if (question.scipDataKey === ScipDataManager.ScipNumber) {
            return uniformStatus(
                (isArticleQuestion.value === "Article as such or Complex Object") &&
                (
                    this._producedInEuScipNumberDependentAnswers.map(a => a.toLowerCase()).includes(producedInEuQuestion.value.toLowerCase()) ||
                    this._hasScipNumberScipNumberDependentAnswers.map(a => a.toLowerCase()).includes(hasScipNumberQuestion.value.toLowerCase())
                )
            );
        }
        else if (question.summaryFieldKey === ScipDataManager.ComplexObjectOrArticleSummaryKey) {
            return uniformStatus(this.isComplexObjectOrStraightArticle());
        }
        else if (question.scipDataKey === ScipDataManager.ScipCharacteristics) {
            // characteristics are never required even when visible
            return {
                required: false,
                visible: this.isComplexObject() || this.isStraightArticle()
            }
        }
        else {
            return uniformStatus(this.isComplexObject() || this.isStraightArticle());
        }
    }

    public questionIsVisible(q: QuestionnaireQuestion) {
        return this.getQuestionStatus(q).visible;
    }

    protected customIsQuestionRequired(question: QuestionnaireQuestion) {
        return this.getQuestionStatus(question).required;
    }

    public isComplexObjectOrStraightArticle() {
        const isArticleQuestion = this._questions.find(q => q.summaryFieldKey === ScipDataManager.IsArticleSummaryKey)!;
        const producedInEuQuestion = this.getQuestionByScipData(ScipDataManager.ScipDataKeyEuProduction);
        const hasScipNumberQuestion = this.getQuestionByScipData(ScipDataManager.HasScipNumber);

        return (
            (isArticleQuestion.value === "Article as such or Complex Object") &&
            (producedInEuQuestion.value === "No data") &&
            (hasScipNumberQuestion.value === "No")
        );
    }

    public isComplexObject() {
        const isComplexObjectOrArticleQuestion = this._questions.find(q => q.summaryFieldKey === ScipDataManager.ComplexObjectOrArticleSummaryKey)!;

        return (
            this.isComplexObjectOrStraightArticle() &&
            (isComplexObjectOrArticleQuestion.value === "Complex Object")
        );
    }

    public isStraightArticle() {
        const isComplexObjectOrArticleQuestion = this._questions.find(q => q.summaryFieldKey === ScipDataManager.ComplexObjectOrArticleSummaryKey)!;

        return (
            this.isComplexObjectOrStraightArticle() &&
            (isComplexObjectOrArticleQuestion.value === "Article, as such")
        );
    }

    public getPageRequiredInfoLocalized() {
        if (this.shouldShowScip()) {
            return null;
        }

        return "SCIP data is not required because the product was declared as having no Substances of Very High Concern (SVHC) above 0.1% wt/wt. If this this not correct, update the SVHC decision."
    }
}
